// Bottom-left account footer (all 3 shells via the Sidebar footer slot). Inline
// Reibul/Cattr-style block: avatar (with a green presence dot when active) +
// display name over a `role · status` line, the whole block linking to My
// account, with an inline sign-out icon button on the right. Real-auth drives
// identity (GET /auth/me); in maket mode it falls back to the shell persona
// label and hides sign-out (the mock role-switcher is the identity there).

import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { PexIcon } from './icons/PexIcon'
import { USE_REAL_API } from '../lib/api'
import { fetchMe, type MeResponse } from '../lib/auth'
import { useAuthStore } from '../store/auth'
import { useT } from '../i18n'
import { ACCOUNT_STRINGS } from '../i18n/shared/account'
import { useSidebarCollapsed } from './sidebarCollapsedContext'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const SHELL_KEY = {
  '/admin': 'admin',
  '/seller': 'seller',
  '/supply': 'supply',
} as const

// role = persona_label → mapped org_type → shell label. status = Active when the
// account is_active, else the raw user status. Maket mode has no membership, so
// the caller's shell label carries the role and there is no status.
function roleLine(
  me: MeResponse | null | undefined,
  shellLabel: string,
): string {
  const m = me?.active_membership
  if (m?.persona_label) return m.persona_label
  if (m?.org_type) return m.org_type
  return shellLabel
}

export function AccountMenu({
  shellBase,
  fallbackName,
}: {
  shellBase: '/admin' | '/seller' | '/supply'
  fallbackName: string
}) {
  const t = useT(ACCOUNT_STRINGS)
  const navigate = useNavigate()
  const collapsed = useSidebarCollapsed()

  const accessToken = useAuthStore((s) => s.accessToken)
  const clearTokens = useAuthStore((s) => s.clearTokens)

  const meQ = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 60_000,
  })

  const shellLabel = t.roleLabels[SHELL_KEY[shellBase]]
  const name = meQ.data?.user.display_name || fallbackName
  const isActive = meQ.data?.user.is_active ?? false
  const role = roleLine(meQ.data, shellLabel)
  const status = meQ.data
    ? isActive
      ? t.statusActive
      : meQ.data.user.status
    : ''

  return (
    <div className={`flex items-center gap-2 px-2 py-2 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
      <Link
        to={`${shellBase}/account`}
        title={collapsed ? name : undefined}
        aria-label={collapsed ? name : undefined}
        data-rail-label={collapsed ? name : undefined}
        className={`flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-[var(--sidebar-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)] ${collapsed ? 'lg:justify-center lg:flex-none lg:p-1' : ''}`}
      >
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--sidebar-avatar-bg)] text-xs font-semibold text-[var(--sidebar-parent-text)]">
          {name ? initials(name) : <PexIcon name="account" size="md" tone="inherit" />}
          {isActive && (
            <span
              className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--sidebar-bg)] bg-emerald-500"
              aria-hidden="true"
            />
          )}
        </span>
        <span className={`min-w-0 flex-1 ${collapsed ? 'lg:sr-only' : ''}`}>
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-medium text-[var(--sidebar-parent-text)]">{name}</span>
            <span
              className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--sidebar-child-text)]"
            >
              {shellLabel}
            </span>
          </span>
          <span className="block truncate text-xs text-[var(--sidebar-child-text)]">
            {status ? `${role} · ${status}` : role}
          </span>
        </span>
      </Link>
      {accessToken && (
        <button
          type="button"
          onClick={() => {
            clearTokens()
            navigate({ to: '/login' })
          }}
          className={`shrink-0 rounded-md p-2 text-[var(--sidebar-icon)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-parent-text)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)] ${collapsed ? 'lg:hidden' : ''}`}
          aria-label={t.logout}
          title={t.logout}
        >
          <PexIcon name="signOut" size="xs" tone="inherit" />
        </button>
      )}
    </div>
  )
}
